/**
 * Cloud history — last 25 generations saved server-side for the
 * signed-in user. Distinct from the existing local-storage History
 * which is per-device; cloud history follows the user across devices.
 *
 * Loaded on demand (only when the modal opens) to keep the initial
 * page payload lean. Reloads into the main panel via the same shape
 * the local history uses.
 */
import { useEffect, useState } from 'react';
import type { DomainOption } from '../types';

interface CloudHistoryEntry {
  id: string;
  prompt: string;
  optionsJson: DomainOption[];
  model: string;
  createdAt: string;
}

interface CloudHistoryProps {
  onLoad: (entry: { request: string; options: DomainOption[] }) => void;
  onClose: () => void;
}

export function CloudHistory({ onLoad, onClose }: CloudHistoryProps) {
  const [entries, setEntries] = useState<CloudHistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetch('/api/history?limit=25', { credentials: 'same-origin' })
      .then(async (res) => {
        const json = await res.json();
        if (!res.ok) throw new Error(json?.error?.message || 'Failed to load history');
        if (alive) setEntries(json.generations ?? []);
      })
      .catch((err) => {
        if (alive) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function fmt(iso: string) {
    const d = new Date(iso);
    return d.toLocaleString();
  }

  return (
    <div className="modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal cloud-history" onClick={(e) => e.stopPropagation()}>
        <div className="modal__tabs">
          <span className="modal__tab modal__tab--active">Cloud history</span>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal__body">
          <p className="modal__intro">
            Your last 25 generations across all devices. Saved when you're signed in.
          </p>
          {error && <div className="modal__error">{error}</div>}
          {entries === null && !error && <p>Loading…</p>}
          {entries !== null && entries.length === 0 && (
            <p className="muted">No saved generations yet. Generate something while signed in to populate.</p>
          )}
          {entries !== null && entries.length > 0 && (
            <ul className="cloud-history__list">
              {entries.map((e) => (
                <li key={e.id} className="cloud-history__item">
                  <div className="cloud-history__row">
                    <span className="cloud-history__date">{fmt(e.createdAt)}</span>
                    <button
                      type="button"
                      className="button button--ghost button--small"
                      onClick={() => {
                        onLoad({ request: e.prompt, options: e.optionsJson });
                        onClose();
                      }}
                    >
                      Reload
                    </button>
                  </div>
                  <div className="cloud-history__prompt">{e.prompt}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
